/**
 * Detecta navegadores in-app (Instagram, Facebook, TikTok) donde `fetch()`
 * a PostHog analytics mostró ~30% de fallos "Load failed" / "Failed to fetch"
 * al crear sesiones Stripe en iOS Mobile Safari.
 *
 * En esos entornos usamos navegación full-page (form POST + redirect 303)
 * en lugar de fetch JSON.
 */

function ua(): string {
  if (typeof navigator === "undefined") return "";
  return navigator.userAgent || "";
}

import { flushPostHogBeforeNavigation } from "@/lib/posthogAnalytics";

/** Instagram / Facebook / Meta in-app WebView en iOS. */
export function isMetaInAppBrowser(): boolean {
  const u = ua();
  if (!u) return false;
  return /FBAN|FBAV|Instagram/i.test(u);
}

/** iOS (iPhone/iPad/iPod) — incluye Mobile Safari e in-app browsers. */
export function isIOSDevice(): boolean {
  const u = ua();
  if (!u) return false;
  return /iPhone|iPad|iPod/i.test(u) || (/\bMac\b/.test(u) && navigator.maxTouchPoints > 1);
}

/**
 * true → preferir form POST + redirect del API en vez de fetch().
 * Cubre el cluster PostHog: vip_membership_video_mux + Mobile Safari + Load failed.
 */
export function shouldUseNavigationSubscriptionCheckout(): boolean {
  if (typeof window === "undefined") return false;
  if (isMetaInAppBrowser()) return true;
  // iOS Safari también falla fetch en algunos casos (ITP / cross-origin quirks)
  if (isIOSDevice() && /Safari/i.test(ua()) && !/Chrome|CriOS|FxiOS/i.test(ua())) {
    return true;
  }
  return false;
}

type SubscriptionCheckoutFormPayload = Record<string, string | number | boolean | undefined>;

/**
 * POST full-page a /api/create-subscription-checkout con redirect=1.
 * El servidor responde 303 → Stripe Checkout (sin fetch en el cliente).
 */
export async function submitSubscriptionCheckoutViaForm(
  payload: SubscriptionCheckoutFormPayload,
): Promise<void> {
  // Flush events for coherence with the rest of the checkout funnel integration.
  await flushPostHogBeforeNavigation(300);

  const form = document.createElement("form");
  form.method = "POST";
  form.action = "/api/create-subscription-checkout?redirect=1";
  form.style.display = "none";
  form.setAttribute("accept-charset", "UTF-8");

  for (const [key, raw] of Object.entries(payload)) {
    if (raw === undefined || raw === null) continue;
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = key;
    input.value = typeof raw === "object" ? JSON.stringify(raw) : String(raw);
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
}